import React, { useState, useEffect } from "react";
import { usersService } from "@/services/usersService";
import { rolesService } from "@/services/rolesService";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2, Plus, X, Eye } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const UserManagement = () => {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  // Form states
  const [newUsername, setNewUsername] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [selectedRoleToAdd, setSelectedRoleToAdd] = useState("");
  const [selectedRolesOnCreation, setSelectedRolesOnCreation] = useState([]);
  const [creatingUser, setCreatingUser] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);

  // Fetch users and roles on mount
  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await usersService.list();
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast({
        title: "Error",
        description: "Failed to fetch users",
        variant: "destructive",
      });
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await rolesService.list();
      setRoles(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch roles:", error);
      setRoles([]);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const response = await usersService.get(userId);
      setUserDetails(response.data);
      setSelectedUser(userId);
    } catch (error) {
      console.error("Error fetching user details:", error);
      toast({
        title: "Error",
        description: "Failed to fetch user details",
        variant: "destructive",
      });
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUsername.trim() || !newEmail.trim()) {
      toast({
        title: "Error",
        description: "Username and email are required",
        variant: "destructive",
      });
      return;
    }

    setCreatingUser(true);
    toast({
      title: "Creating user...",
      description: "Please wait while the user is being created",
    });

    try {
      const response = await usersService.create({
        username: newUsername,
        email: newEmail,
      });
      const result = response.data;
      const userId = result.user.user_id;

      // Assign selected roles to the new user
      if (selectedRolesOnCreation.length > 0) {
        let successCount = 0;
        let failedRoles = [];

        for (const roleId of selectedRolesOnCreation) {
          try {
            await usersService.assignRole(userId, parseInt(roleId));
            successCount++;
            console.log(`✓ Assigned role ${roleId} to user`);
          } catch (err) {
            failedRoles.push(`Role ${roleId}`);
            console.error(`✗ Error assigning role ${roleId}:`, err);
          }
        }

        // Show result
        if (failedRoles.length === 0) {
          toast({
            title: "✓ Success",
            description: `User created with ${successCount} role(s) assigned!`,
          });
        } else {
          toast({
            title: "⚠ Partial Success",
            description: `User created: ${successCount}/${selectedRolesOnCreation.length} roles assigned. Issues: ${failedRoles.join("; ")}`,
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "✓ Success",
          description: "User created successfully",
        });
      }

      setNewUsername("");
      setNewEmail("");
      setSelectedRolesOnCreation([]);
      fetchUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Failed to create user";
      toast({
        title: "✗ Error",
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setCreatingUser(false);
    }
  };

  const handleAddRole = async () => {
    if (!selectedUser || !selectedRoleToAdd) return;

    try {
      await usersService.assignRole(selectedUser, parseInt(selectedRoleToAdd));
      toast({
        title: "Success",
        description: "Role assigned to user",
      });
      setSelectedRoleToAdd("");
      fetchUserDetails(selectedUser);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Failed to assign role";
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
    }
  };

  const toggleRoleSelection = (roleId) => {
    setSelectedRolesOnCreation((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const handleRemoveRole = async (roleId) => {
    if (!selectedUser) return;

    try {
      await usersService.removeRole(selectedUser, roleId);
      toast({
        title: "Success",
        description: "Role removed from user",
      });
      fetchUserDetails(selectedUser);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Failed to remove role";
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget?.user_id) return;

    try {
      await usersService.delete(deleteTarget.user_id);
      toast({
        title: "Success",
        description: "User deleted successfully",
      });
      fetchUsers();
      setSelectedUser(null);
      setUserDetails(null);
      setDeleteDialogOpen(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Failed to delete user";
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
    }
  };

  const getUnassignedRoles = () => {
    if (!userDetails || !Array.isArray(userDetails.roles)) return roles;
    const assignedRoleIds = userDetails.roles.map((r) => r.role_id);
    return roles.filter((r) => !assignedRoleIds.includes(r.role_id));
  };

  return (
    <div className="min-h-screen bg-black text-white px-3 sm:px-4 md:px-8 py-6 md:py-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-8 font-manrope">User Management</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {/* Create User Section */}
          <Card className="bg-zinc-950 border-zinc-900">
            <CardHeader>
              <CardTitle className="font-manrope">Create New User</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">
                    Username
                  </label>
                  <Input
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    placeholder="e.g., john.doe"
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="john@example.com"
                    className="bg-zinc-900 border-zinc-800 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">
                    Select Roles (Optional)
                  </label>
                  <div className="space-y-2 bg-zinc-900 border border-zinc-800 rounded-lg p-3">
                    {roles.length === 0 ? (
                      <p className="text-xs text-zinc-500">No roles available</p>
                    ) : (
                      roles.map((role) => (
                        <div key={role.role_id} className="flex items-center gap-2">
                          <Checkbox
                            id={`role-${role.role_id}`}
                            checked={selectedRolesOnCreation.includes(role.role_id)}
                            onCheckedChange={() => toggleRoleSelection(role.role_id)}
                            className="border-zinc-600 bg-zinc-800"
                          />
                          <label
                            htmlFor={`role-${role.role_id}`}
                            className="text-sm text-zinc-300 cursor-pointer"
                          >
                            {role.role_name}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                  {selectedRolesOnCreation.length > 0 && (
                    <p className="text-xs text-zinc-500 mt-2">
                      {selectedRolesOnCreation.length} role(s) selected
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={creatingUser}
                  className="w-full bg-brand-500 hover:bg-brand-600 disabled:bg-brand-700 disabled:cursor-not-allowed"
                >
                  {creatingUser ? "Creating..." : "Create User"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Users List */}
          <Card className="bg-zinc-950 border-zinc-900">
            <CardHeader>
              <CardTitle className="font-manrope">Existing Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {users.length === 0 ? (
                  <p className="text-zinc-500 text-sm">No users found</p>
                ) : (
                  users.map((user) => (
                    <div
                      key={user.user_id}
                      onClick={() => fetchUserDetails(user.user_id)}
                      className={`p-3 rounded-lg cursor-pointer transition ${
                        selectedUser === user.user_id
                          ? "bg-brand-500 text-white"
                          : "bg-zinc-900 hover:bg-zinc-800 text-zinc-300"
                      }`}
                    >
                      <div className="font-medium">{user.username}</div>
                      <div className="text-xs mt-1 opacity-75">
                        {user.email}
                      </div>
                      <div className="text-xs mt-1 opacity-50">
                        {user.roles?.length || 0} role(s)
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* User Details Section */}
        {selectedUser && userDetails && (
          <Card className="bg-zinc-950 border-zinc-900 mt-8">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="font-manrope">
                    {userDetails.user.full_name || userDetails.user.username}
                  </CardTitle>
                  <p className="text-sm text-zinc-400 mt-2">
                    {userDetails.user.email}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Dialog open={viewDetailsOpen} onOpenChange={setViewDetailsOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Eye className="w-4 h-4 mr-2" />
                        View Access
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-zinc-950 border-zinc-900 max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>
                          {userDetails.user.username}'s Accessible Pages
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        {userDetails.accessible_pages?.length === 0 ? (
                          <p className="text-zinc-500 text-sm">
                            No pages accessible to this user
                          </p>
                        ) : (
                          <div className="grid gap-2">
                            {userDetails.accessible_pages?.map((page) => (
                              <div
                                key={page.page_name}
                                className="bg-zinc-900 p-3 rounded-lg"
                              >
                                <div className="font-medium">
                                  {page.page_label}
                                </div>
                                <p className="text-xs text-zinc-500">
                                  {page.page_path}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      setDeleteTarget(userDetails.user);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete User
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Assign Role Section */}
              <div>
                <h3 className="text-lg font-semibold mb-4 font-manrope">
                  Assign Roles to User
                </h3>
                <div className="flex gap-2 mb-4">
                  <Select
                    value={selectedRoleToAdd}
                    onValueChange={setSelectedRoleToAdd}
                  >
                    <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white">
                      <SelectValue placeholder="Select role to assign" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {getUnassignedRoles().map((role) => (
                        <SelectItem
                          key={role.role_id}
                          value={role.role_id.toString()}
                        >
                          {role.role_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleAddRole}
                    className="bg-brand-500 hover:bg-brand-600"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>
              </div>

              {/* Assigned Roles */}
              <div>
                <h3 className="text-lg font-semibold mb-4 font-manrope">
                  Assigned Roles ({userDetails.roles?.length || 0})
                </h3>
                {userDetails.roles?.length === 0 ? (
                  <p className="text-zinc-500 text-sm">
                    No roles assigned to this user
                  </p>
                ) : (
                  <div className="space-y-2">
                    {userDetails.roles?.map((role) => (
                      <div
                        key={role.role_id}
                        className="flex items-center justify-between bg-zinc-900 p-3 rounded-lg"
                      >
                        <div>
                          <Badge className="bg-purple-600 text-white">
                            {role.role_name}
                          </Badge>
                          <p className="text-xs text-zinc-500 mt-1">
                            {role.description || "No description"}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveRole(role.role_id)}
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-zinc-950 border-zinc-900">
          <AlertDialogTitle>Delete User</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the user "{deleteTarget?.username}"
            ? This action cannot be undone.
          </AlertDialogDescription>
          <div className="flex justify-end gap-4 mt-6">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserManagement;
